import React from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import { makeStyles } from "@Compat/makeStyles";
import { alpha, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

// The consent UI shows on the marketing site and in the app, under any
// theme: its colours come from the active palette, not the old cobalt.
const accentGradient = (theme: Theme) =>
    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`;

const useStyles = makeStyles((theme) => ({
    root: {
        position: "fixed",
        zIndex: theme.zIndex.snackbar + 2,
        left: "50%",
        bottom: theme.spacing(3),
        transform: "translateX(-50%)",
        width: "min(960px, calc(100vw - 32px))",
        padding: theme.spacing(3, 3.5),
        borderRadius: 28,
        backdropFilter: "blur(16px)",
        backgroundColor:
            alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.86 : 0.92),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.13)}`,
        boxShadow:
            theme.palette.mode === "dark"
                ? "0 32px 80px rgba(0, 0, 0, 0.45)"
                : `0 28px 72px ${alpha(theme.palette.primary.dark, 0.18)}`,
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        [theme.breakpoints.up("md")]: {
            flexDirection: "row",
            alignItems: "center",
            gap: theme.spacing(3)
        },
        // Phones: a compact strip — the full banner covered half the first
        // screen, including the hero's main CTA.
        [theme.breakpoints.down("sm")]: {
            bottom: theme.spacing(1),
            width: "calc(100vw - 16px)",
            padding: theme.spacing(1.75, 2),
            borderRadius: 20,
            gap: theme.spacing(1.25)
        }
    },
    copy: {
        flex: 1
    },
    title: {
        fontWeight: 800,
        marginBottom: theme.spacing(0.5),
        [theme.breakpoints.down("sm")]: {
            display: "none"
        }
    },
    text: {
        color: theme.palette.text.secondary,
        fontSize: "0.92rem",
        lineHeight: 1.5,
        [theme.breakpoints.down("sm")]: {
            fontSize: "0.8rem",
            lineHeight: 1.45
        }
    },
    // The long explanation on tablets and up; one line on phones.
    longText: {
        [theme.breakpoints.down("sm")]: { display: "none" }
    },
    shortText: {
        display: "none",
        [theme.breakpoints.down("sm")]: { display: "inline" }
    },
    link: {
        color: theme.palette.primary.main,
        cursor: "pointer",
        fontWeight: 600
    },
    actions: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1.5),
        [theme.breakpoints.down("sm")]: {
            gap: theme.spacing(1),
            "& > *": { flex: "1 1 0" }
        }
    },
    pill: {
        "&&": {
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 700,
            whiteSpace: "nowrap",
            padding: theme.spacing(1, 3),
            [theme.breakpoints.down("sm")]: {
                padding: theme.spacing(0.75, 1.5),
                fontSize: "0.85rem"
            }
        }
    },
    // Phones: "Customize" drops to a small text link on its own row under
    // Reject / Accept, which keep equal weight side by side (EDPB/CNIL).
    customizePill: {
        "&&": {
            [theme.breakpoints.down("sm")]: {
                order: 3,
                flex: "1 1 100%",
                border: "none",
                padding: theme.spacing(0.25, 1),
                fontWeight: 600,
                fontSize: "0.8rem"
            }
        }
    },
    acceptPill: {
        "&&": {
            color: theme.palette.primary.contrastText,
            background: accentGradient(theme),
            boxShadow: `0 14px 32px ${alpha(theme.palette.primary.main, 0.36)}`,
            "&:hover": { background: accentGradient(theme), transform: "translateY(-1px)", boxShadow: `0 18px 38px ${alpha(theme.palette.primary.main, 0.48)}` }
        }
    }
}));

interface CookieConsentBannerProps {
    onAcceptAll: () => void;
    onRejectAll: () => void;
    onCustomize: () => void;
    onOpenPolicy: (kind: "cookie" | "privacy") => void;
    // Lets a consumer (e.g. the marketing site) measure the banner's real
    // rendered height and reserve that much space at the bottom of the
    // page, so this fixed-position banner can never sit on top of a CTA
    // that happens to land in the last ~200px of the viewport.
    rootRef?: React.Ref<HTMLDivElement>;
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
    onAcceptAll,
    onRejectAll,
    onCustomize,
    onOpenPolicy,
    rootRef
}) => {
    const classes = useStyles();
    const { t } = useTranslation("CookieConsent");

    return (
        <Box ref={rootRef} className={classes.root} role="dialog" aria-label={t("banner.title")} aria-live="polite">
            <Box className={classes.copy}>
                <Typography className={classes.title}>{t("banner.title")}</Typography>
                <Typography className={classes.text}>
                    <span className={classes.longText}>{t("banner.text")}</span>
                    <span className={classes.shortText}>{t("banner.shortText")}</span>{" "}
                    <Link className={classes.link} component="button" onClick={() => onOpenPolicy("cookie")}>
                        {t("banner.cookiePolicy")}
                    </Link>
                    {" · "}
                    <Link className={classes.link} component="button" onClick={() => onOpenPolicy("privacy")}>
                        {t("banner.privacyPolicy")}
                    </Link>
                </Typography>
            </Box>
            <Box className={classes.actions}>
                {/* Reject is given the same visual weight as Accept (EDPB/CNIL). */}
                <Button className={`${classes.pill} ${classes.customizePill}`} variant="outlined" onClick={onCustomize}>
                    {t("customize")}
                </Button>
                <Button className={classes.pill} variant="contained" color="inherit" onClick={onRejectAll}>
                    {t("rejectAll")}
                </Button>
                <Button className={`${classes.pill} ${classes.acceptPill}`} onClick={onAcceptAll}>
                    {t("acceptAll")}
                </Button>
            </Box>
        </Box>
    );
};

export default CookieConsentBanner;
